<template>
  <div class="flex flex-col md:pt-20">
    <div
      class="w-full md:w-2/3 md:max-w-screen-lg flex flex-row bg-white rounded-sm shadow mx-auto mt-5 md:mt-0"
    >
      <div class="flex flex-col py-10 px-5 md:py-20 md:px-16 w-full md:w-1/2">
        <h2 class="text-4xl text-center font-light py-5">Login</h2>
        <form @submit.prevent="login">
          <div class="flex flex-col pb-5">
            <label class="pb-1 text-sm">Email</label>
            <input
              v-model="username"
              type="email"
              class="w-full px-4 text-sm md:text-base border-1 border-brand-background-light-blue shadow-sm rounded placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green"
            />
          </div>
          <div class="flex flex-col pb-5 relative">
            <div class="flex flex-row row pb-1 justify-between">
              <label class="text-sm">Password</label>
              <router-link
                tag="a"
                :to="{ name: 'home_password_reset' }"
                class="text-sm text-brand-gray hover:underline hover:text-brand-black focus:ring-brand-green"
                >Forgot Password?</router-link
              >
            </div>
            <input
              v-model="password"
              :type="show ? 'text' : 'password'"
              class="w-full px-4 text-sm md:text-base border-1 border-brand-background-light-blue shadow-sm rounded placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green"
            />
            <EyeIcon
              class="absolute h-7 w-7 text-brand-green top-7 md:top-8 right-3 z-50 cursor-pointer"
              v-if="!show"
              @click="toggleShow"
            />
            <EyeOffIcon
              v-else
              class="absolute h-7 w-7 text-brand-dark-gray top-7 md:top-8 right-3 z-50 cursor-pointer"
              @click="toggleShow"
            />
          </div>
          <div class="flex flex-col pb-5" v-if="showAuthError">
            <div
              class="rounded-sm border border-brand-red mx-auto py-2 px-3 bg-brand-background-light-red"
              style="background: #f8dade"
            >
              <p class="text-brand-red text-sm py-1">Username and password is required.</p>
            </div>
          </div>
          <div class="flex flex-col pb-5" v-if="getAuthError">
            <div
              v-if="wrongLogin"
              class="rounded-sm border border-brand-red mx-auto py-2 px-3"
              style="background: #f8dade"
            >
              <p class="text-brand-red text-sm py-1">
                Sorry, the entered username or password is incorrect.
              </p>
              <p class="text-brand-red text-sm py-1">
                For security reason, after {{ attemptsLeft }} more failed login attempts you'll have
                to wait 30 minutes before trying again.
              </p>
            </div>
            <div
              v-if="locked"
              class="rounded-sm border border-brand-red mx-auto py-2 px-3"
              style="background: #f8dade"
            >
              <p class="text-brand-red text-sm py-1">
                No more than {{ maxAttempts }} failed login attempts in a 30 minute period are
                allowed. Please try again later.
              </p>
            </div>
          </div>
          <div class="flex flex-col pb-5">
            <div class="mx-auto w-full md:w-2/3">
              <PrimaryButton
                @click="login"
                :disabled="isAuthLoading || isUserLoading"
                :is-loading="isAuthLoading || isUserLoading"
              >
                Login
              </PrimaryButton>
            </div>
            <router-link
              tag="a"
              :to="{ name: 'home_signup' }"
              class="py-5 text-brand-gray underline text-center hover:text-brand-black"
              >Don't have an account? Sign Up</router-link
            >
          </div>
        </form>
      </div>
      <div class="hidden md:flex py-16">
        <div class="border-l-2 border-brand-background-light-blue"></div>
      </div>
      <div class="hidden md:flex flex-col py-20 px-12 w-1/2">
        <img class="w-16 pt-5 pb-16" src="@/assets/images/paper-plane.png" />
        <h2 class="text-5xl text-brand-gray w-full py-1">It's nice</h2>
        <h2 class="text-5xl text-brand-gray py-1">to see</h2>
        <h2 class="text-5xl text-brand-gray py-1">you again!</h2>
      </div>
      <div></div>
    </div>
  </div>
  <Footer />
</template>
<script>
import * as Sentry from "@sentry/vue"

import { AUTH_LOGIN, USER_GET } from "@/store/types"
import { mapGetters } from "vuex"

import { EyeIcon, EyeOffIcon } from "@heroicons/vue/outline"

export default {
  components: {
    EyeIcon,
    EyeOffIcon
  },
  computed: {
    ...mapGetters([
      "getCurrentUser",
      "getAuthError",
      "isAuthenticated",
      "isAuthLoading",
      "isUserLoading"
    ]),
    attemptsLeft() {
      return this.maxAttempts - this.attemptsUsed
    }
  },
  data() {
    return {
      username: "",
      password: "",
      errorMessage: "",
      maxAttempts: 0,
      locked: false,
      wrongLogin: false,
      attemptsUsed: 0,
      showAuthError: false,
      show: false
    }
  },
  async mounted() {
    if (this.isAuthenticated && !this.getCurrentUser) {
      try {
        await this.$store.dispatch(USER_GET)
        if (this.getCurrentUser) {
          if (
            this.classicToggleEnabled &&
            this.getCurrentUser.landlord &&
            !this.getCurrentUser.landlord.edge_enabled
          ) {
            location.href = `${process.env.VUE_APP_CLASSIC_BASE_URI}/login`
            return
          }

          if (this.getCurrentUser.applicant && !this.getCurrentUser.landlord) {
            this.$router.push({ name: "applicant_dashboard_applications" })
          } else {
            if (this.getCurrentUser.active_app_count > 0) {
              this.$router.push({ name: "dashboard_applicants" })
            } else {
              this.$router.push({ name: "dashboard_forms" })
            }
          }
        }
      } catch (err) {
        console.log(err)
      }
    }
  },
  methods: {
    toggleShow() {
      this.show = !this.show
    },
    async login() {
      const { username, password } = this
      if (!username || !password) {
        this.showAuthError = true
        return
      }

      try {
        await this.$store.dispatch(AUTH_LOGIN, {
          username: username.toLowerCase(),
          password
        })
        await this.$store.dispatch(USER_GET)

        if (this.getCurrentUser.landlord && this.getCurrentUser.landlord.force_password_reset) {
          this.$router.push({
            name: "home_password_reset_confirm",
            params: { uidb64: "force", token: localStorage.getItem("token") }
          })
          return
        }

        if (this.$route.params.viewApp) {
          this.$router.push({
            name: "dashboard_view_application",
            params: { id: this.$route.params.viewApp }
          })
        } else if (this.getCurrentUser.applicant && !this.getCurrentUser.landlord) {
          this.$router.push({ name: "applicant_dashboard_applications" })
        } else {
          if (this.getCurrentUser.active_app_count > 0) {
            this.$router.push({ name: "dashboard_applicants_submitted" })
          } else {
            this.$router.push({ name: "dashboard_forms" })
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-unused-vars
        const error = err.response

        if (!error.data) {
          Sentry.captureException(err, {
            extra: {
              response: error
            }
          })
        }

        this.maxAttempts = parseInt(error.data.attempts_max)

        if (err) {
          if (error.data.locked === "True") {
            this.locked = true
            this.wrongLogin = false
          } else {
            this.wrongLogin = true
            this.locked = false
            this.attemptsUsed = parseInt(error.data.attempts_used)
          }
        }
      }
    }
  }
}
</script>
